import axios from 'axios';
import apiConfig from '../config/api.config';

/**
 * Axios instance oluştur - config'den ayarları al
 */
const apiClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers
});

/**
 * Request interceptor - Hata yönetimi için
 */
apiClient.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Hata yönetimi
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ Response Error:', error.response || error);
    return Promise.reject(error);
  }
);

const newsService = {
  /**
   * Haber listesini çeker
   * @param {string} category - Kategori URL'i (genel-haberler, spor-haberleri, vb.)
   * @param {number} page - Sayfa numarası
   * @param {boolean} sortAscending - Sıralama düzeni (varsayılan: false - en yeni ilk)
   * @returns {Promise} API response
   */
  async getNewsList(category, page = 1, sortAscending = false, filters = null) {
    try {
      const requestBody = apiConfig.formatRequestBody({
        url: category,
        page,
        sortBy: 'Date',
        sortAscending,
        filters
      });
      
      const response = await apiClient.post(apiConfig.endpoint, requestBody);

      if (response.data.isSuccess) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          errors: response.data.errors,
          errorCode: response.data.errorCode
        };
      }
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        errors: [error.message],
        errorCode: error.response?.status || 500
      };
    }
  },

  /**
   * Retry mekanizması ile haber listesi çekimi
   */
  async getNewsListWithRetry(category, page = 1, sortAscending = false, filters = null) {
    const { count, delay } = apiConfig.retry;
    
    for (let i = 0; i < count; i++) {
      try {
        const result = await this.getNewsList(category, page, sortAscending, filters);
        if (result.success) return result;
        
        // Son deneme değilse bekle ve tekrar dene
        if (i < count - 1) {
          const waitTime = delay * Math.pow(2, i);
          console.log(`⏳ Retry ${i + 1}/${count} after ${waitTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        } else {
          return result;
        }
        
      } catch (error) {
        if (i === count - 1) throw error;
        
        // Exponential backoff
        const waitTime = delay * Math.pow(2, i);
        console.log(`⏳ Retry ${i + 1}/${count} after ${waitTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  },

  /**
   * Tüm kategorilerdeki haberleri çeker (Ana sayfa için)
   */
  async getAllNews(page = 1) {
    const categories = ['genel-haberler', 'spor-haberleri', 'teknoloji-haberleri', 'ekonomi-haberleri'];
    
    try {
      const requests = categories.map(category => 
        this.getNewsList(category, page)
      );
      
      const responses = await Promise.all(requests);
      
      // Tüm haberleri birleştir ve tarihe göre sırala
      const allNews = responses
        .filter(res => res.success)
        .flatMap((res, index) => 
          res.data.map(item => ({
            ...item,
            _category: categories[index]
          }))
        )
        .sort((a, b) => new Date(b.properties.Date) - new Date(a.properties.Date));
      
      return {
        success: true,
        data: allNews
      };
    } catch (error) {
      console.error('Error fetching all news:', error);
      return {
        success: false,
        errors: [error.message]
      };
    }
  }
};

export default newsService;
